"use client";

import { useMemo, useState } from "react";
import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { FaqRichText } from "@/components/faq/faq-richtext";
import { resolveBg } from "@/lib/bg-color";
import { sectionSpacing } from "@/components/optilase/section-spacing";
import { useFaqDrawer } from "@/lib/faq-context";
import { topicLabel } from "@/lib/faq-topics";
import { faqAnswer, faqInRegion, topicSlug, topicUrl, type FaqStory } from "@/lib/faqs";
import { regionFromPathname, regionPath } from "@/lib/region";
import { usePublicPathname } from "@/lib/public-pathname";

export type FaqQuestionsBlok = SbBlokData & {
  title?: string;
  description?: string;
  topic?: string;
  max_questions?: string | number;
  questions?: string[];
  bg_color?: unknown;
  spacing?: string;
};

function ArrowIcon() {
  return (
    <svg width="17" height="11" viewBox="0 0 17 11" fill="none" aria-hidden>
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function dominantTopic(faqs: FaqStory[]): string {
  const counts = new Map<string, number>();
  for (const f of faqs) {
    const topic = topicSlug(f.content?.topic);
    if (topic) counts.set(topic, (counts.get(topic) ?? 0) + 1);
  }
  let best = "";
  for (const [topic, n] of counts) if (n > (counts.get(best) ?? 0)) best = topic;
  return best;
}

export default function FaqQuestionsView({
  blok,
  faqs,
  topicCounts,
}: {
  blok: FaqQuestionsBlok;
  faqs: FaqStory[];
  topicCounts: Record<string, number>;
}) {
  const { bg } = resolveBg(blok.bg_color, "white");
  const { faqs: liveFaqs } = useFaqDrawer();
  const contextFaqs = useMemo(() => {
    const live = new Map(liveFaqs.map((f) => [f.uuid, f]));
    return faqs.map((f) => live.get(f.uuid) ?? f);
  }, [faqs, liveFaqs]);
  const pathname = usePublicPathname();
  const region = regionFromPathname(pathname);
  const limit =
    typeof blok.max_questions === "string"
      ? parseInt(blok.max_questions, 10) || 0
      : (blok.max_questions ?? 0);
  const targetTopic = blok.topic ? topicSlug(blok.topic) : "";
  const fullList = useMemo<FaqStory[]>(() => {
    if (targetTopic) {
      const inTopic = contextFaqs.filter(
        (f) => topicSlug(f.content?.topic) === targetTopic && faqInRegion(f, region),
      );
      const order = new Map(
        (Array.isArray(blok.questions) ? blok.questions : []).map((uuid, i) => [uuid, i]),
      );
      const rank = (f: FaqStory) => order.get(f.uuid) ?? order.size;
      return [...inTopic].sort((a, b) => rank(a) - rank(b));
    }
    if (Array.isArray(blok.questions) && blok.questions.length > 0) {
      const byUuid = new Map(contextFaqs.map((f) => [f.uuid, f]));
      return blok.questions
        .map((uuid) => byUuid.get(uuid))
        .filter((f): f is FaqStory => f !== undefined)
        .filter((f) => faqInRegion(f, region));
    }
    return [];
  }, [targetTopic, blok.questions, contextFaqs, region]);
  const questions = useMemo(
    () => (limit > 0 ? fullList.slice(0, limit) : fullList),
    [fullList, limit],
  );
  const linkTopic = targetTopic || dominantTopic(fullList);
  const topicTotal = targetTopic ? fullList.length : (topicCounts[linkTopic] ?? 0);
  const shownInTopic = questions.filter((f) => topicSlug(f.content?.topic) === linkTopic).length;
  const hasMore = Boolean(linkTopic) && shownInTopic < topicTotal;
  const linkHref = hasMore ? topicUrl(linkTopic, region) : regionPath("/faqs", region);
  const linkLabel = hasMore
    ? `View all ${topicLabel(linkTopic).replace(/ questions$/i, "")} questions`
    : linkTopic
      ? "See other FAQ topics"
      : "Browse all FAQs";
  const [openUid, setOpenUid] = useState<string | null>(questions[0]?.uuid ?? null);

  if (questions.length === 0) return null;

  return (
    <section {...storyblokEditable(blok)} className={`${bg} px-5 md:px-10 lg:px-20`}>
      <div className={`mx-auto w-full max-w-7xl ${sectionSpacing(blok.spacing)}`}>
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-[21px]">
          <div className="flex flex-col gap-5 text-black lg:w-[468px] lg:shrink-0 lg:pr-[93px]">
            {blok.title && <h2 className="text-section text-teal-07">{blok.title}</h2>}
            {blok.description && (
              <p className="text-body leading-[1.56] text-[#5c5c66]">{blok.description}</p>
            )}
            <a
              href={linkHref}
              className="text-body text-teal-08 hidden w-fit items-center gap-1.75 py-2.5 leading-[1.054] font-extrabold hover:underline lg:inline-flex"
            >
              {linkLabel}
              <ArrowIcon />
            </a>
          </div>

          <div className="flex flex-1 flex-col gap-6 lg:gap-0">
            <ul className="flex flex-col">
              {questions.map((faq, i) => {
                const isOpen = openUid === faq.uuid;
                const isFirst = i === 0;
                const isLast = i === questions.length - 1;
                return (
                  <li
                    key={faq.uuid}
                    className={`border-t ${
                      isFirst ? "border-teal-04" : "border-teal-03"
                    } ${isLast ? "border-b-teal-03 border-b" : ""}`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenUid(isOpen ? null : faq.uuid)}
                      aria-expanded={isOpen}
                      className="flex w-full cursor-pointer items-start justify-between gap-6 py-5 text-left"
                    >
                      <span className="text-body lg:text-h6 flex-1 leading-[1.56] font-bold text-[#212126]">
                        {faq.name}
                      </span>
                      <span aria-hidden className="text-teal-07 shrink-0 text-[28px] leading-none">
                        {isOpen ? "–" : "+"}
                      </span>
                    </button>
                    <div
                      className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                        isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="text-body pr-0 pb-8 leading-[1.56] text-[#5c5c66] lg:pr-12">
                          <FaqRichText node={faqAnswer(faq, region)} />
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            <a
              href={linkHref}
              className="text-body text-teal-08 inline-flex w-fit items-center gap-1.75 py-2.5 leading-[1.054] font-extrabold hover:underline lg:hidden"
            >
              {linkLabel}
              <ArrowIcon />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
